/*
 Copyright (c) 2025. BrightNight. All rights reserved.
 Unauthorized copying of this file, via any medium is strictly prohibited.
 Proprietary and confidential.
 */

import ArrowRightIcon from '@mui/icons-material/ArrowCircleRightRounded';
import { Box, Divider, Grid, Skeleton, Typography, useTheme } from '@mui/material';
import { Stack } from '@mui/system';
import { coreServiceToFlow, FlowRunState, type FlowRunStatus, paFlows } from '@poweralpha/common';
import dayjs from 'dayjs';
import { uniqueId } from 'lodash';
import { useCallback, useMemo } from 'react';
import usePANavigate from '../react-components/navigate/usePANavigate';
import { trimString } from '../utils';
import { useGetScenarioIdMutation } from './api';
import { getRunStyling, isFlowRunning, RUN_ENTRY_HEIGHT } from './const';
import { getLogsLink, getRecordLink, parseProjectIdFromTags } from './utils';

function SimulationEntry({ run, onClick }: Readonly<{ run: FlowRunStatus; onClick: () => void }>) {
  const { baseSx, clickableSx, icon } = getRunStyling(useTheme(), run);
  const { navigate } = usePANavigate();

  const { mutateAsync: getScenarioId } = useGetScenarioIdMutation({
    onSuccess: ([{ scenario_id }]) => {
      navigate(getRecordLink(run, { scenarioId: scenario_id }));
      onClick();
    },
  });

  const isClickable = useMemo(
    () =>
      [FlowRunState.COMPLETED, FlowRunState.FAILED, FlowRunState.CRASHED].includes(run.state.type),
    [run.state.type],
  );

  const handleClick = useCallback(() => {
    if (!isClickable) return;
    if (run.state.type !== FlowRunState.COMPLETED) {
      const projectId = parseProjectIdFromTags(run.tags);
      navigate(getLogsLink(run.flow_name, projectId));
      return;
    }

    const scenarioIdFlows = [...paFlows, ...Object.values(coreServiceToFlow)];
    if (scenarioIdFlows.includes(run.flow_name)) {
      getScenarioId([run.id]);
      return;
    }

    navigate(getRecordLink(run));
  }, [getScenarioId, run, isClickable, navigate]);

  return useMemo(
    () => (
      <>
        <Box onClick={handleClick} key={`run-${run.id}`} sx={{ position: 'relative' }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={RUN_ENTRY_HEIGHT}
            animation="wave"
            data-visual-test="transparent"
            sx={{
              visibility: isFlowRunning(run) ? 'visible' : 'hidden',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 0,
              bgcolor: 'inherit',
            }}
          />
          <Box sx={isClickable ? clickableSx : baseSx}>
            <ArrowRightIcon
              className="run-arrow"
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0,
                transition: 'opacity 0.2s',
                zIndex: 1,
              }}
            />
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              {icon}
              <Grid>
                <Typography variant="body1">{trimString(run.flow_name, 35)}</Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    alignItems: 'center',
                    height: 15,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontSize: 12 }}>
                    {dayjs(run.created).fromNow()}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                    •
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 12,
                    }}
                  >
                    {trimString(run.name, 25)}
                  </Typography>
                </Stack>
              </Grid>
            </Stack>
          </Box>
        </Box>
        <Divider key={uniqueId('divider')} />
      </>
    ),
    [run, baseSx, clickableSx, isClickable, icon, handleClick],
  );
}

export default SimulationEntry;
